import { useContext, useCallback, useMemo } from 'react';
import { useLogger } from '../context/useLogger';
import { EdiRole, UserInfosType } from '../types/UserInfos';
import { UserInfoContext } from '../context/UserInfoQueryContext.context';

export const useUserInfos = () => {
  const context = useContext(UserInfoContext);
  const logger = useLogger();

  if (context === undefined) {
    throw new Error('useUserInfos must be used within a UserInfoProvider');
  }

  const hasRole = useCallback(
    (roles?: EdiRole[], valueIfEmpty: boolean = false) => {
      const userInfo = context.userInfos;
      const isLoadingUserInfos = context.isLoading;

      logger.info(`📥 UserInfos context [hasRole]`);

      if (isLoadingUserInfos) {
        return valueIfEmpty;
      }

      if (roles === undefined || roles.length === 0) {
        return valueIfEmpty;
      }

      logger.debug(
        `📥 UserInfos context [userInfos]: ${userInfo ? JSON.stringify(userInfo) : '<null>'}`,
      );

      if (!userInfo) {
        return false;
      }

      let role: EdiRole;

      if (userInfo.IsExploit) {
        role = 'exploit';
      } else if (userInfo.canViewAdminMenuLink) {
        role = 'admin';
      } else {
        role = 'user';
      }

      return roles.includes(role);
    },
    [context.userInfos, context.isLoading, logger],
  );

  const isRegistered = useCallback(() => {
    const userInfo = context.userInfos;
    const codeOctave = userInfo?.OctaveCode;

    logger.debug(
      `📥 UserInfos context OctaveCode [currentUserData]: ${userInfo ? userInfo.OctaveCode : '<null>'}`,
    );

    if (codeOctave === undefined) {
      return false;
    }

    return codeOctave !== '';
  }, [context.userInfos, logger]);

  const userInfosData: UserInfosType = useMemo(
    () => ({
      userInfos: context.userInfos,
      isLoading: context.isLoading,
      refetchUserInfo: context.refetchUserInfo,
      handleLogout: context.handleLogout,
      hasRole: hasRole,
      isRegistered: isRegistered,
      getAccessToken: context.getAccessToken,
    }),
    [context, hasRole, isRegistered],
  );

  return userInfosData;
};
